import { useState, useId } from 'react';
import { Button } from './ui/button';
import { Upload, X, Image as ImageIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadImageToR2 } from '../lib/r2Upload';

export default function ImageUpload({ onUploadComplete, currentImage, label = "Şəkil yüklə", folder = "uploads" }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentImage || null);
  const inputId = useId();

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Yalnız şəkil faylları yükləyə bilərsiniz');
      return;
    }

    // Validate file size (5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Şəkil 5MB-dan kiçik olmalıdır');
      return;
    }

    // Show local preview immediately
    const localPreview = URL.createObjectURL(file);
    setPreview(localPreview);

    // Upload to R2
    setUploading(true);
    try {
      console.log('Uploading to R2...', file.name);
      const imageUrl = await uploadImageToR2(file, folder);
      console.log('Upload successful:', imageUrl);
      
      // Update preview with actual R2 URL
      setPreview(imageUrl);
      onUploadComplete(imageUrl);
      toast.success('Şəkil yükləndi');
      
      // Clean up local preview
      URL.revokeObjectURL(localPreview);
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Şəkil yüklənə bilmədi');
      setPreview(currentImage || null);
      URL.revokeObjectURL(localPreview);
    } finally {
      setUploading(false);
      // Reset input
      e.target.value = '';
    }
  };

  const handleRemove = () => {
    setPreview(null);
    onUploadComplete('');
  };

  return (
    <div className="space-y-3">
      {preview ? (
        <div className="relative">
          <img
            src={preview}
            alt="Preview"
            className="w-full h-48 object-cover rounded-lg border border-border"
          />
          {uploading && (
            <div className="absolute inset-0 bg-black/50 rounded-lg flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-white animate-spin" />
            </div>
          )}
          <Button
            type="button"
            variant="destructive"
            size="sm"
            className="absolute top-2 right-2"
            onClick={handleRemove}
            disabled={uploading}
          >
            <X className="w-4 h-4" />
          </Button>
        </div>
      ) : (
        <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
          <ImageIcon className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
          <p className="text-sm text-muted-foreground mb-3">
            PNG, JPG, GIF - maksimum 5MB
          </p>
        </div>
      )}

      <div>
        <input
          type="file"
          id={inputId}
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={uploading}
        />
        <Button
          type="button"
          variant="outline"
          className="w-full"
          disabled={uploading}
          onClick={() => document.getElementById(inputId).click()}
        >
          {uploading ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Yüklənir...
            </>
          ) : (
            <>
              <Upload className="w-4 h-4 mr-2" />
              {label}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
